import { createAsyncThunk } from '@reduxjs/toolkit';

import { APP_ROUTE } from '@backrs_ui/grow-ui/src/App';
import { LubavSessionType } from '@backrs_ui/grow-ui/src/models/models';

import { AxiosInstance } from 'axios';

import { CostsState } from './coachLubav';
import { getSessionState } from '../lubavSessionState/lubavSessionState.utils';
import { RootState } from '../store';

type Increments = Partial<CostsState>;

const getSessionTypeFromPath = (
  path: string,
  solutionSessionName?: string | null,
  officeHoursId?: string,
): string | undefined => {
  return (
    officeHoursId ||
    solutionSessionName ||
    (path === APP_ROUTE.OFFICE_HOURS_SUPERUSER && 'office_hours_superuser') ||
    (path === APP_ROUTE.OFFICE_HOURS_SUPERCOACH && 'office_hours_supercoach') ||
    undefined
  );
};

export const incrementCostsThunk = createAsyncThunk<
  CostsState,
  Increments & { sessionType: LubavSessionType },
  { state: RootState }
>('coachLubav/incrementCosts', async ({ sessionType, ...increments }, { getState }) => {
  const currentCosts = getSessionState(getState(), sessionType)?.coachLubav.costs;

  if (!currentCosts) throw new Error('No session state found');

  const newCosts: CostsState = {
    total_cost: currentCosts.total_cost + (increments.total_cost ?? 0),
    prompt_tokens: currentCosts.prompt_tokens + (increments.prompt_tokens ?? 0),
    completion_tokens: currentCosts.completion_tokens + (increments.completion_tokens ?? 0),
  };

  return newCosts;
});

export const initiateSession = createAsyncThunk(
  'coachLubav/initiateSession',
  async (
    {
      path,
      userId,
      solutionSessionName,
      officeHoursId,
      axios,
      activeSession,
    }: {
      path: string;
      userId: number;
      solutionSessionName?: string | null;
      officeHoursId?: string;
      axios: AxiosInstance;
      activeSession: LubavSessionType | null;
    },
    { rejectWithValue },
  ) => {
    try {
      const sessionType = getSessionTypeFromPath(path, solutionSessionName, officeHoursId);

      if (!sessionType) return;

      const response = await axios.post(
        '/api/initiate-session',
        {
          user_id: userId,
          session_type: sessionType,
        },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        },
      );

      const result = {
        sessionId: response.data,
        sessionType,
        activeSession,
      };

      return result;
    } catch (error) {
      return rejectWithValue(`Failed to fetch session id with error: ${error}`);
    }
  },
);
